import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Users, Package, AlertCircle, Receipt, Sparkles } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatINR } from "@/lib/currency";

interface Product {
  id: string;
  title: string;
  handle: string;
  status: string;
  image: string | null;
  price: string;
  shakatahEnabled: boolean;
  metafieldId: string | null;
}

interface ProductShakatahManagerProps {
  merchantId: string;
  hasToken: boolean;
}

interface ProductSettings {
  auto_enable_new_products: boolean;
  default_tax_rate: number;
}

const ProductShakatahManager = ({ merchantId, hasToken }: ProductShakatahManagerProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [bulkToggling, setBulkToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ProductSettings>({
    auto_enable_new_products: false,
    default_tax_rate: 0,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [merchantId]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('auto_enable_new_products, default_tax_rate')
        .eq('id', merchantId)
        .single();

      if (!error && data) {
        setSettings({
          auto_enable_new_products: data.auto_enable_new_products ?? false,
          default_tax_rate: data.default_tax_rate ?? 0,
        });
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const handleSettingSave = async (field: keyof ProductSettings, value: any) => {
    setSavingSettings(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({ [field]: value })
        .eq('id', merchantId);

      if (error) throw error;
      
      setSettings(prev => ({ ...prev, [field]: value }));
      toast.success("Setting saved");
    } catch (err: any) {
      console.error("Error saving setting:", err);
      toast.error("Failed to save setting");
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    if (hasToken) {
      fetchProducts();
    }
  }, [hasToken, merchantId]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError("Please sign in to manage products");
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke("manage-product-metafield", {
        body: { merchantId, action: "list" },
      });

      if (fnError) {
        throw fnError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setProducts(data.products || []);
    } catch (err: any) {
      console.error("Error fetching products:", err);
      setError(err.message || "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (productId: string, currentEnabled: boolean) => {
    setToggling(productId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to manage products");
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke("manage-product-metafield", {
        body: { 
          merchantId, 
          action: "toggle",
          productId,
          enabled: !currentEnabled,
        },
      });

      if (fnError) {
        throw fnError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      // Update local state
      setProducts(prev => 
        prev.map(p => 
          p.id === productId ? { ...p, shakatahEnabled: !currentEnabled } : p
        )
      );

      toast.success(
        !currentEnabled 
          ? "Split Payment enabled for this product" 
          : "Split Payment disabled for this product"
      );
    } catch (err: any) {
      console.error("Error toggling product:", err);
      toast.error(err.message || "Failed to update product");
    } finally {
      setToggling(null);
    }
  };

  const handleBulkToggle = async (enableAll: boolean) => {
    setBulkToggling(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to manage products");
        return;
      }

      const productsToUpdate = products.filter(p => p.shakatahEnabled !== enableAll);
      
      if (productsToUpdate.length === 0) {
        toast.info(enableAll ? "All products are already enabled" : "All products are already disabled");
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const product of productsToUpdate) {
        try {
          const { data, error: fnError } = await supabase.functions.invoke("manage-product-metafield", {
            body: { 
              merchantId, 
              action: "toggle",
              productId: product.id,
              enabled: enableAll,
            },
          });

          if (fnError || data.error) {
            failCount++;
          } else {
            successCount++;
          }
        } catch {
          failCount++;
        }
      }

      // Update local state
      setProducts(prev => 
        prev.map(p => ({ ...p, shakatahEnabled: enableAll }))
      );

      if (failCount === 0) {
        toast.success(`${enableAll ? 'Enabled' : 'Disabled'} split payments for ${successCount} products`);
      } else {
        toast.warning(`Updated ${successCount} products, ${failCount} failed`);
      }
    } catch (err: any) {
      console.error("Error bulk toggling products:", err);
      toast.error("Failed to update products");
    } finally {
      setBulkToggling(false);
    }
  };

  const enabledCount = products.filter(p => p.shakatahEnabled).length;
  const allEnabled = products.length > 0 && enabledCount === products.length;
  const allDisabled = products.length > 0 && enabledCount === 0;

  if (!hasToken) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Product Split Payments
          </CardTitle>
          <CardDescription>
            Configure your Shopify Admin API token to manage split payments for your products
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please configure your Shopify Admin API access token above to enable this feature.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Product Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Product Settings
          </CardTitle>
          <CardDescription>
            Configure default settings for split payments on products
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Auto-enable New Products</Label>
                  <p className="text-xs text-muted-foreground">Automatically enable split payments for new products added to your store</p>
                </div>
                <Switch
                  checked={settings.auto_enable_new_products}
                  onCheckedChange={(checked) => handleSettingSave('auto_enable_new_products', checked)}
                  disabled={savingSettings}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Product List Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Product Split Payments
                {enabledCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {enabledCount} enabled
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                Enable split payment for products your customers can pay together
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {products.length > 0 && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleBulkToggle(true)}
                    disabled={loading || bulkToggling || allEnabled}
                  >
                    {bulkToggling ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : null}
                    Enable All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleBulkToggle(false)}
                    disabled={loading || bulkToggling || allDisabled}
                  >
                    {bulkToggling ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : null}
                    Disable All
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchProducts}
                disabled={loading || bulkToggling}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading products...</span>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No products found in your store</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {products.map((product) => (
              <div 
                key={product.id}
                className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
              >
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-12 h-12 rounded-md object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
                    <Package className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.title}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{formatINR(parseFloat(product.price))}</span>
                    {product.status !== "ACTIVE" && (
                      <Badge variant="outline" className="text-xs">
                        {product.status.toLowerCase()}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {product.shakatahEnabled && (
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      <Users className="h-3 w-3 mr-1" />
                      Split Enabled
                    </Badge>
                  )}
                  <Switch
                    checked={product.shakatahEnabled}
                    onCheckedChange={() => handleToggle(product.id, product.shakatahEnabled)}
                    disabled={toggling === product.id}
                  />
                  {toggling === product.id && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductShakatahManager;
